import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import Dialog from "components/Dialog";
import LoginForm from "components/LoginForm";
import NewMemberForm, { RenderUserFields } from "components/NewMemberForm";
import {
  initialEditingUser,
  setIsEditingPrimaryUser,
  setPrimaryUser,
  setSecondaryUsers,
} from "features/slices/buyFlowSlice";
import { setStep } from "features/slices/shopPagePhaseTwoSlice";
import { BuyFlowUser } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { trackConversion } from "queries/conversion.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Pressable, View } from "react-native";
import { emailExistsInSecondaryUsers } from "screens/Register/registerUtils";
import userServices from "services/user.services";
import { getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import { getUserName } from "utils/userUtils";
import { planIdProperty } from "./helpers";
import useAuth from "hooks/useAuth";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import { Formik } from "formik";
import { object } from "yup";

const StepThree = forwardRef(function StepThree(
  { createAccount }: { createAccount: () => void },
  ref: any
) {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const isLoggedIn = auth.isLoggedIn;

  const [addMemberFormVisible, setAddMemberFormVisible] = useState(false);
  const [loginFormVisible, setLoginFormVisible] = useState(false);
  const [addMemberFormInitialValues, setAddMemberFormInitialValues] =
    useState<BuyFlowUser>(initialEditingUser);

  // When user is editing a member, this will be the index of the member being edited
  const [editMemberIndex, setEditMemberIndex] = useState<number | null>(null);

  const { selectedCmsPlan, step } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const { isEditingPrimaryUser, primaryUser, secondaryUsers } = useAppSelector(
    (state) => state.buyFlow
  );

  const { mutateAsync: track } = useMutation(trackConversion);

  const { data: planPackages } = useFetchPlanPackagesQuery();

  const {
    planPackageUserFields,
    planPackageFieldsValidation,
    fieldRefs: planPackageUserFieldsRefs,
  } = usePlanPackageUserFields(selectedCmsPlan?.[planIdProperty]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (isEditingPrimaryUser) {
      dispatch(setIsEditingPrimaryUser(false));
    }
    if (secondaryUsers.length > 0) {
      dispatch(setSecondaryUsers([]));
    }
    if (addMemberFormVisible) {
      setAddMemberFormVisible(false);
    }
  }, [
    addMemberFormVisible,
    dispatch,
    isEditingPrimaryUser,
    isLoggedIn,
    secondaryUsers.length,
  ]);

  const planType = () => {
    const selectedPlanPackage = planPackages?.find(
      (p) => p.id === selectedCmsPlan![planIdProperty]
    );
    if (!selectedPlanPackage) return null;
    return getPlanType(selectedPlanPackage);
  };

  const selectedPlanType = planType();
  const allowPrimaryUserEdit = !isLoggedIn;
  const allowAddMembers = !isLoggedIn && selectedPlanType === "RESERVATION";
  const showEditForm = isEditingPrimaryUser && !isLoggedIn;
  const showContinueButton = isLoggedIn && step === 3;

  const handlePrimaryUserSubmit = async (values: BuyFlowUser) => {
    const emailChanged =
      values.email?.toLowerCase() !== primaryUser.email?.toLowerCase();
    if (emailChanged) {
      const userExisted = await userServices.userExists(values.email!);
      if (userExisted) {
        toast.error(
          `An account with email ${values.email} already exists. Please log in instead.`,
        );
        return;
      }
    }
    dispatch(setPrimaryUser(values));
    dispatch(setIsEditingPrimaryUser(false));
    dispatch(setStep(step + 1));
    await track({ step: "USER_INFO_ENTERED", flow: "SHOP_PHASE_TWO" });
  };

  const handleMemberSubmit = async (values: BuyFlowUser) => {
    if (editMemberIndex !== null) {
      const existingEmail = secondaryUsers[editMemberIndex].email;
      const emailChanged =
        values.email!.toLowerCase() !== existingEmail?.toLowerCase();
      if (emailChanged) {
        if (values.email!.toLowerCase() === primaryUser.email!.toLowerCase()) {
          toast.error("Email is used by the primary member");
          return;
        }
        if (
          emailExistsInSecondaryUsers(
            values.email!,
            secondaryUsers.filter((_, i) => i !== editMemberIndex),
          )
        ) {
          toast.error("Email is used by another member");
          return;
        }
        const userExisted = await userServices.userExists(values.email!);
        if (userExisted) {
          toast.error(`Email ${values.email} is already registered`);
          return;
        }
      }
      const newMembers = [...secondaryUsers];
      newMembers[editMemberIndex] = values;
      dispatch(setSecondaryUsers(newMembers));
      setEditMemberIndex(null);
    } else {
      if (values.email!.toLowerCase() === primaryUser.email!.toLowerCase()) {
        toast.error("Email is used by the primary member");
        return;
      }
      if (emailExistsInSecondaryUsers(values.email!, secondaryUsers)) {
        toast.error("Email is used by another member");
        return;
      }
      const userExisted = await userServices.userExists(values.email!);
      if (userExisted) {
        toast.error(`Email ${values.email} is already registered`);
        return;
      }

      dispatch(setSecondaryUsers([...secondaryUsers, values]));
    }
    setAddMemberFormVisible(false);
  };

  return (
    <div ref={ref}>
      <Text category="h6">Personal Information</Text>
      {(!isEditingPrimaryUser || isLoggedIn) && (
        <View className="bg-white rounded-xl p-4 my-4 shadow-lg font-semibold text-lg">
          <View className="flex-row items-center justify-between">
            <View>
              <span>{getUserName(primaryUser)}</span>
              <span>{primaryUser.email?.toLowerCase()}</span>
              <span>{primaryUser.phone}</span>
            </View>
            {allowPrimaryUserEdit && (
              <Button
                appearance="ghost"
                onPress={() => dispatch(setIsEditingPrimaryUser(true))}
              >
                Edit
              </Button>
            )}
          </View>

          {allowAddMembers && (
            <Button
              appearance="ghost"
              onPress={() => {
                setAddMemberFormVisible(true);
                setAddMemberFormInitialValues(initialEditingUser);
                setEditMemberIndex(null);
              }}
            >
              Add Member(Additional Plan)
            </Button>
          )}

          {allowAddMembers && addMemberFormVisible && (
            <View>
              <div className="mb-4">Additional Member Info:</div>
              <NewMemberForm
                initialValues={addMemberFormInitialValues}
                onSubmit={handleMemberSubmit}
                showImageUploader={false}
                btnText={isEditingPrimaryUser ? "Update User" : "Add User"}
              />
              <View className="mt-4">
                <Button onPress={() => setAddMemberFormVisible(false)}>
                  Cancel
                </Button>
              </View>
            </View>
          )}
          {showContinueButton && (
            <Formik
              initialValues={primaryUser}
              onSubmit={async (values) => {
                dispatch(setPrimaryUser(values));
                dispatch(setStep(step + 1));
                await track({
                  step: "USER_INFO_ENTERED",
                  flow: "SHOP_PHASE_TWO",
                });
              }}
              validationSchema={object({
                ...planPackageFieldsValidation,
              })}
              validateOnMount
            >
              {({ handleSubmit, isValid }) => (
                <View>
                  {planPackageUserFields?.map((planPackageUserField, i) => {
                    const {
                      id,
                      field: { name, type },
                    } = planPackageUserField;
                    return (
                      <RenderUserFields
                        key={id}
                        name={name}
                        type={type}
                        userField={planPackageUserField}
                        userFieldRefs={planPackageUserFieldsRefs}
                        index={i}
                      />
                    );
                  })}
                  <View className="mt-4">
                    <Button
                      disabled={!isValid}
                      onPress={() => handleSubmit()}
                    >
                      Continue
                    </Button>
                  </View>
                </View>
              )}
            </Formik>
          )}
        </View>
      )}
      {showEditForm && (
        <View className="mt-4">
          <NewMemberForm
            initialValues={primaryUser}
            onSubmit={handlePrimaryUserSubmit}
            includePassword
            showImageUploader={false}
            btnText={primaryUser.email ? "Update User" : "Add User"}
          />
          <View className="flex-row gap-4 mt-2">
            <Pressable onPress={() => setLoginFormVisible(true)}>
              <Text status="info">Login</Text>
            </Pressable>
            <Pressable
              onPress={() => (window.location.href = "/forgot_password")}
            >
              <Text status="info">Forgot Password</Text>
            </Pressable>
          </View>
          <Dialog
            visible={loginFormVisible}
            onCancel={() => setLoginFormVisible(false)}
            cancelText="Close"
            style={{ backgroundColor: "#ffffff", borderRadius: 20, padding: 24 }}
          >
            <LoginForm allowSignup={false} />
          </Dialog>
        </View>
      )}
      {/* start of additional members section */}
      {!isLoggedIn && secondaryUsers.length > 0 && (
        <View className="bg-white rounded-xl p-4 my-4 shadow-lg font-semibold text-lg">
          <div>Additional Members:</div>
          {secondaryUsers.map((member, i) => (
            <View
              key={i}
              className="flex-row items-center justify-between my-6"
            >
              <View>
                <span>{getUserName(member)}</span>
                <span>{member.email?.toLowerCase()}</span>
                <span>{member.phone}</span>
              </View>
              <View>
                <Button
                  appearance="ghost"
                  onPress={() => {
                    setAddMemberFormVisible(true);
                    setAddMemberFormInitialValues(member);
                    setEditMemberIndex(i);
                  }}
                >
                  Edit
                </Button>
                <Button
                  appearance="ghost"
                  onPress={() =>
                    dispatch(
                      setSecondaryUsers(
                        secondaryUsers.filter(
                          (_, secondaryUserIndex) => secondaryUserIndex !== i
                        )
                      )
                    )
                  }
                >
                  Delete
                </Button>
              </View>
            </View>
          ))}
        </View>
      )}
    </div>
  );
});

export default StepThree;
